import React, { useState, useEffect } from "react";
import { IonIcon, IonItem } from "@ionic/react";
import { closeCircleOutline } from "ionicons/icons";
import { AllCountryCodes } from "../../utils/AllCountryCodes";
import SelectionModal from "./popups/SelectionModal";
import { getCountryDetails } from "../home/EnvironmentalDataManager";
import { ionicStorage } from "../../services/storageService";
import { useStorage } from "../../contexts/StorageContext";
import { Preferences } from "@capacitor/preferences";

const SimpleInput: React.FC<{
  value: string;
  onChange: (val: string) => void;
  isValid: boolean;
  setEmailMode: (val: boolean) => void;
  invalidCountryCode?: boolean;
  onClearOrRemove: () => void;
  isFirstInput: boolean;
  showClearButton: boolean;
  countryCode?: string;
  onCountryCodeChange: (code: string) => void;
  placeholder?: string;
}> = ({
  value,
  onChange,
  isValid,
  setEmailMode,
  invalidCountryCode,
  onClearOrRemove,
  isFirstInput,
  showClearButton,
  countryCode,
  onCountryCodeChange,
  placeholder = "Email or Phone Number",
}) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [isEmailMode, setIsEmailMode] = useState(true);
  const [codeModal, setCodeModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countryCodeState, setCountrycode] = useState("+1");
  const [countryCodeManuallySet, setCountryCodeManuallySet] = useState(false);

  const [userLocation, setUserLocation] = useState<
    {
      code: string;
      country: string;
      dateFormat: string;
      timeZone: string;
    }[]
  >([]);
  useEffect(() => {
    const getUserLocation = async () => {
      const Location: any = await getCountryDetails();

      await Preferences.set({
        key: "userLocation",
        value: JSON.stringify(Location),
      });
      setUserLocation(Location);
    };
    // getUserLocation();
  }, []);

  // Sync with prop value changes
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  // Sync with prop countryCode changes
  useEffect(() => {
    if (countryCodeState) {
      if (onCountryCodeChange) {
        onCountryCodeChange(countryCodeState);
      }
    }
  }, [countryCodeState]);

  const formatByPattern = (countryCode: string, digits: string) => {
    if (digits.length === 0) {
      return "";
    }
    const country =
      AllCountryCodes[countryCode as keyof typeof AllCountryCodes];
    if (!country) return digits;

    // Special handling for US/Canada - return only formatted number without country code
    if (countryCode === "+1") {
      if (digits.length <= 3) {
        return `(${digits}`;
      } else if (digits.length <= 6) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      } else {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(
          6
        )}`;
      }
    }

    // Parse the pattern to understand how to format
    const pattern = country.pattern;
    const digitPattern = pattern.replace(countryCode, "").trim();

    // Parse pattern to extract formatting groups
    const formatGroups: {
      type: "digits" | "separator";
      count?: number;
      char?: string;
    }[] = [];
    let currentGroup = "";
    for (let i = 0; i < digitPattern.length; i++) {
      const char = digitPattern[i];
      if (char === "#") {
        currentGroup += "#";
      } else if (char === " " || char === "-" || char === "(" || char === ")") {
        if (currentGroup) {
          formatGroups.push({ type: "digits", count: currentGroup.length });
          currentGroup = "";
        }
        if (char !== " ") {
          formatGroups.push({ type: "separator", char: char });
        } else {
          formatGroups.push({ type: "separator", char: " " });
        }
      }
    }
    if (currentGroup) {
      formatGroups.push({ type: "digits", count: currentGroup.length });
    }

    // Apply formatting based on parsed groups - return only formatted number
    let result = "";
    let digitPos = 0;
    let isFirstGroup = true;

    for (const group of formatGroups) {
      if (group.type === "digits" && typeof group.count === "number") {
        const groupDigits = digits.slice(digitPos, digitPos + group.count);
        if (groupDigits.length > 0) {
          // Don't add space before first group, but add space before subsequent digit groups
          if (
            !isFirstGroup &&
            result &&
            !result.endsWith(" ") &&
            !result.endsWith("(") &&
            !result.endsWith("-")
          ) {
            result += " ";
          }
          result += groupDigits;
          digitPos += group.count;
          isFirstGroup = false;
        }
        if (digitPos >= digits.length) break;
      } else if (group.type === "separator" && digitPos < digits.length) {
        const remainingDigits = digits.slice(digitPos);
        if (remainingDigits.length > 0 && group.char) {
          result += group.char;
        }
      }
    }

    return result;
  };

  const formatPhoneNumber = (value: string, countryCode: string) => {
    const cleaned = value.replace(/[^\d]/g, "");
    return formatByPattern(countryCode, cleaned);
  };

  const isPhoneInput = (input: string) => {
    const trimmed = input.trim();

    // Definitely not phone if contains @ or clear email patterns
    if (trimmed.includes("@")) return false;
    if (/^[a-zA-Z][a-zA-Z0-9]*\.[a-zA-Z]/.test(trimmed)) return false;
    if (/^[a-zA-Z][a-zA-Z0-9._-]+$/.test(trimmed) && /[._-]/.test(trimmed))
      return false;
    const numeric = trimmed.replace(/\D/g, "");

    if (numeric.length > 0) return true;

    // Must be mostly or all digits to be considered phone
    if (/^\d+$/.test(trimmed) && trimmed.length >= 4) return true;

    // Phone patterns with formatting, but must be mostly digits
    const phonePattern = /^[\d\s\-()+]*$/;
    const digitCount = (trimmed.match(/\d/g) || []).length;
    const totalLength = trimmed.length;

    // At least 70% digits and follows phone pattern
    return (
      phonePattern.test(trimmed) &&
      digitCount >= 1 &&
      digitCount / totalLength >= 0.1
    );
  };

  const handleInputChange = (inputValue: string) => {
    const trimmedInput = inputValue.trim();
    if (inputValue === "") {
      setIsEmailMode(true);
      setEmailMode(true);
      onChange("");
      return;
    }
    // Update local state
    setInputValue(inputValue);

    if (!trimmedInput) {
      setIsEmailMode(true);
      setEmailMode(true);
      onChange("");
      return;
    }

    // Clean for phone check: remove spaces, (), and -
    const phoneCheckInput = trimmedInput.replace(/[()\s-]/g, "");

    // Allow phone mode ONLY if it's all numbers (with optional + at start)
    const isPhone = /^\+?\d+$/.test(phoneCheckInput);

    if (isPhone) {
      setIsEmailMode(false);
      setEmailMode(false);

      // Set country code based on user location or default to +1
      if (!countryCodeState && userLocation.length > 0) {
        setCountrycode(userLocation[0]?.code || "+1");
      } else if (!countryCodeState) {
        setCountrycode("+1");
      }

      // Format phone number
      if (countryCodeState) {
        const digitsOnly = phoneCheckInput.replace(/[^\d]/g, "");
        const formattedPhone = formatPhoneNumber(digitsOnly, countryCodeState);
        onChange(formattedPhone);
      } else {
        onChange(phoneCheckInput);
      }
    } else {
      // Handle as email (default behavior)
      setIsEmailMode(true);
      setEmailMode(true);

      // Remove () - and spaces for email too
      const emailSanitized = trimmedInput.replace(/[()\s-]/g, "").toLowerCase();
      onChange(emailSanitized);
    }
  };

  const handleCountryCodeSelection = (e: any, fieldName: string) => {
    const selectedCode = e.detail.value;
    setCountrycode(selectedCode);
    setCountryCodeManuallySet(true);
    setCodeModal(false);

    // Notify parent component
    if (onCountryCodeChange) {
      onCountryCodeChange(selectedCode);
    }

    // Reformat current input with new country code
    if (inputValue && !isEmailMode) {
      const cleanedInput = inputValue.replace(/[^\d]/g, "");
      if (cleanedInput) {
        const formatted = formatPhoneNumber(cleanedInput, selectedCode);
        setInputValue(formatted);
        onChange(formatted);
      }
    }
  };

  const renderCountrySelector = () => {
    return (
      <div
        style={{
          position: "relative",
          width: "70px",
          display: "flex",
          alignItems: "center",
          borderRight: "1px solid #e0e0e0",
          paddingRight: "8px",
          marginRight: "8px",
        }}
        onClick={() => {
          setCodeModal(true);
        }}
      >
        <input
          style={{
            width: "100%",
            paddingRight: "10px",
            alignItems: "center",
            marginLeft: "8px",
            fontSize: "16px",
            border: "none",
            outline: "none",
            background: "transparent",
            cursor: "pointer",
          }}
          value={countryCodeState}
          readOnly
        />
        <span
          style={{
            position: "absolute",
            right: "0px",
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
            fontSize: "12px",
            color: "#888",
            display: "flex",
            alignItems: "center",
          }}
        >
          ▼
        </span>
      </div>
    );
  };

  return (
    <>
      <IonItem
        style={{
          "--border-radius": "10px",
          "--border-color": "transparent",
          "--background": "white",
          "--color": "black",
          "--padding-start": "0px",
          "--padding-end": "0px",
          "--inner-padding-end": "0px",
          "--padding-top": "0px",
          "--padding-bottom": "0px",
          "--min-height": "50px",
          borderRadius: "10px",
          marginBottom: "20px",
          width: "100%",
          border: "1px solid rgba(0,0,0,0.2)",
          position: "relative",
          backgroundColor: "white",
          display: "flex",
          alignItems: "center",
        }}
        className="simple-input-item"
        lines="none"
      >
        {!isEmailMode && renderCountrySelector()}

        <input
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          type="text"
          placeholder={placeholder}
          className={`simple-input-field ${
            !isValid || invalidCountryCode ? "input-invalid" : ""
          }`}
          disabled={loading}
          maxLength={isEmailMode ? undefined : 17}
          style={{
            border: "none",
            outline: "none",
            width: "100%",
            borderRadius: "10px",
            background: "transparent",
            flex: 1,
            fontSize: "1rem",
            height: "46px !important",
            padding: "0 12px",
          }}
          autoComplete="off"
          spellCheck={false}
          inputMode="text"
        />

        {showClearButton && (
          <button
            type="button"
            className="clear-btn"
            onClick={onClearOrRemove}
            style={{
              marginRight: "20px",

              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#888",
              fontSize: "1.2rem",
              padding: 0,
            }}
          >
            &times;
          </button>
        )}
      </IonItem>

      {codeModal && (
        <SelectionModal
          isOpen={codeModal}
          onClose={() => setCodeModal(false)}
          title="Select Country Code"
          subtitle="Choose your country code"
          options={Object.entries(AllCountryCodes).map(([code, info]) => ({
            id: code,
            label: `${code} ${info.name}`,
            selected: countryCodeState === code,
          }))}
          showSearch={true}
          fieldName="country_code"
          buttonText="Choose"
          onSave={handleCountryCodeSelection}
          multiSelect={false}
          allowCustomOptions={false}
        />
      )}
    </>
  );
};

export default SimpleInput;
